<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <admin-breadcrumb name="App Notifications"/>
    <b-row>
      <b-col cols="12">
        <admin-panel title="Notifications" sub-title="Manage all app notifications in GreenLists" with-footer>
          <template v-slot:header-right>
            <div>
              <no-permission-wrapper :show="!canCreateNotifications">
                <b-btn
                    variant="primary"
                    @click.prevent="createNewNotification"
                    :disabled="!canCreateNotifications"
                >
                  Create New Notification
                </b-btn>
              </no-permission-wrapper>
            </div>
          </template>

          <admin-app-notifications-table
              :notifications="notifications"
              @edit-clicked="notificationId => editNotification(notificationId)"
              @delete-clicked="notificationId => deleteNotification(notificationId)"
          />
        </admin-panel>
      </b-col>
    </b-row>

    <admin-app-notification-modal
        :visible.sync="modalVisible"
        v-model="notificationFormData"
        :form-validation="notificationFormValidation"
        :form-processing="notificationFormProcessing"
        :forbidden="(!canUpdateNotifications && !!submitNotificationId) || (!canCreateNotifications && !submitNotificationId)"
        :deleted="submitNotificationDeleted"
        @submit="submitNotification"
    />
  </b-container>
</template>

<script>
import AdminPreloader from "../components/AdminPreloader.vue";
import AdminBreadcrumb from "../components/AdminBreadcrumb.vue";
import AdminPanel from "../components/AdminPanel.vue";
import {API_ROUTES} from "../api";
import AdminAppNotificationsTable from "../components/AdminAppNotificationsTable.vue";
import NoPermissionWrapper from "../components/NoPermissionWrapper.vue";
import AdminAppNotificationModal from "../components/AdminAppNotificationModal.vue";
import {convertFromUtc} from "../helpers";

export default {
  name: "AdminAppNotifications",
  components: {
    AdminAppNotificationModal,
    NoPermissionWrapper, AdminAppNotificationsTable, AdminPanel, AdminBreadcrumb, AdminPreloader},
  data() {
    return {
      loading: true,
      notifications: [],
      modalVisible: false,
      notificationFormData: {},
      notificationFormValidation: {},
      notificationFormProcessing: false,
      submitNotificationId: null,
      submitNotificationDeleted: false
    }
  },
  methods: {
    checkPermission(permission) {
      if (!this[permission]) {
        this.$bvToast.toast('Sorry! You don\'t have permission to do this action', {
          title: 'Denied!',
          variant: 'danger'
        });
        return false;
      }
      return true;
    },
    loadNotifications() {
      this.$api.get(API_ROUTES.ADMIN_APP_NOTIFICATIONS).then(({data}) => {
        this.notifications = data.notifications;
      }).catch(({response}) => {
        this.$bvToast.toast('Failed to load notifications', {
          title: 'Error!',
          variant: 'danger'
        });
      }).finally(() => {
        this.loading = false;
      })
    },
    createNewNotification() {
      this.submitNotificationDeleted = false;
      this.submitNotificationId = null;
      this.notificationFormData = {
        title: "",
        content: "",
        level: null,
        start_date: null,
        end_date: null,
        is_fixed: false,
        is_title_hidden: false
      };
      this.modalVisible = true;
    },
    editNotification(notificationId) {
      let notification = this.notifications.find(notification => notification.id === notificationId);
      if (notification) {
        this.submitNotificationDeleted = notification.is_deleted;
        this.submitNotificationId = notificationId;
        this.notificationFormData = {
          id: notification.id,
          title: notification.title,
          content: notification.content,
          level: notification.level,
          start_date: notification.start_date ? convertFromUtc(notification.start_date) : null,
          end_date: notification.end_date ? convertFromUtc(notification.end_date) : null,
          is_fixed: notification.is_fixed,
          is_title_hidden: notification.is_title_hidden
        };
        this.modalVisible = true;
      }
    },
    deleteNotification(notificationId) {
      let notification = this.notifications.find(notification => notification.id === notificationId);
      if (notification) {
        this.$bvModal.msgBoxConfirm("You are going to delete a notification: '" + notification.title + "'", {
          title: 'Please confirm',
          okVariant: 'success',
          centered: true
        }).then(value => {
          if (value) {
            this.loading = true;
            this.$api.post(`${API_ROUTES.ADMIN_APP_NOTIFICATIONS}/${notificationId}/delete`).then(({data}) => {
              let index = this.notifications.findIndex(notification => notification.id === notificationId);
              if (index >= 0) {
                let notifications = [...this.notifications];
                notifications[index] = data.notification
                this.notifications = notifications;
              }
              this.$bvToast.toast('Notification has been successfully deleted', {
                title: 'Success!',
                variant: 'success'
              });
            }).catch(({response: {data}}) => {
              this.$bvToast.toast(data.message ?? 'Failed to delete a notification', {
                title: 'Error!',
                variant: 'danger'
              });
            }).finally(() => {
              this.loading = false;
            });
          }
        });
      }
    },
    submitNotification() {
      if (this.submitNotificationId) {
        if (!this.checkPermission('canUpdateNotifications')) {
          return;
        }
        this.$api.post(`${API_ROUTES.ADMIN_APP_NOTIFICATIONS}/${this.submitNotificationId}`, this.notificationFormData).then(({data}) => {
          let index = this.notifications.findIndex(notification => notification.id === this.submitNotificationId);
          let notifications = [...this.notifications];
          if (index >= 0 ) {
            notifications[index] = data.notification;
          } else {
            notifications.unshift(data.notification);
          }
          this.notifications = notifications;
          this.modalVisible = false;
          this.$bvToast.toast('Notification updated successfully', {
            title: 'Success!',
            variant: 'success'
          });
          this.notificationFormValidation = {};
        }).catch(({response: {data}}) => {
          this.$bvToast.toast(data.message ?? 'Failed to update a notification', {
            title: 'Error!',
            variant: 'danger'
          });
          let validationErrors = {};
          if (typeof data.errors !== 'undefined') {
            for (let fieldName in data.errors) {
              validationErrors[fieldName] = data.errors[fieldName].join(', ');
            }
          }
          this.notificationFormValidation = validationErrors;
        }).finally(() => {
          this.notificationFormProcessing = false;
        });
      } else {
        if (!this.checkPermission('canCreateNotifications')) {
          return;
        }
        this.$api.post(API_ROUTES.ADMIN_APP_NOTIFICATIONS, this.notificationFormData).then(({data}) => {
          let notifications = [...this.notifications];
          notifications.unshift(data.notification);
          this.notifications = notifications;
          this.modalVisible = false;
          this.$bvToast.toast('Notification created successfully', {
            title: 'Success!',
            variant: 'success'
          });
        }).catch(({response: {data}}) => {
          this.$bvToast.toast(data.message ?? 'Failed to create a new notification', {
            title: 'Error!',
            variant: 'danger'
          });
          let validationErrors = {};
          if (typeof data.errors !== 'undefined') {
            for (let fieldName in data.errors) {
              validationErrors[fieldName] = data.errors[fieldName].join(', ');
            }
          }
          this.notificationFormValidation = validationErrors;
        }).finally(() => {
          this.notificationFormProcessing = false;
        });
      }
      this.notificationFormProcessing = true;
    }
  },
  computed: {
    canCreateNotifications() {
      return this.$store.getters['auth/hasAdminPermissions']('app_notification_create');
    },
    canUpdateNotifications() {
      return this.$store.getters['auth/hasAdminPermissions']('app_notification_update');
    },
    canDeleteNotifications() {
      return this.$store.getters['auth/hasAdminPermissions']('app_notification_delete');
    },
  },
  created() {
    this.loadNotifications();
  },
  watch: {
    submitNotificationId(val, oldVal) {
      this.notificationFormValidation = {};
    }
  }
}
</script>
